/**
 * KinsCircle Intelligence Layer V1 — Tree Intelligence Card
 *
 * Command Center operational-authority card that exposes the "Run Tree Intelligence Check"
 * button. When clicked it runs `runTreeHealthCheck` over the loaded
 * family graph (read-only, in-browser, no API calls) and renders a list
 * of issues with severity badges, affected members, likely cause, and a
 * recommended next step.
 *
 * V1 contract:
 *   - Pure UI surface — never mutates anything UNTIL the operator
 *     explicitly clicks "Review Fix" → "Apply Fix" inside ReviewFixModal.
 *   - Render returns null when the intelligence flag is OFF (default).
 *   - Render returns null when the viewer is not owner / admin.
 *   - Pulls members + memories + photos + comments from
 *     FamilyGraphContext via `useFamilyGraph()`.
 *   - Auto-repair / auto-merge / auto-delete remain prohibited. Every
 *     repair requires the modal flow.
 *   - "Undo last repair" surfaces only when an undoable repair exists
 *     in the local repair log (see `hasUndoableRepair`).
 *
 * Honors STABILITY.md §1, §3, §7, §8, §9, and §9.10 (owner-approved
 * repair extension).
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  Brain,
  PlayCircle,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Loader2,
  ShieldCheck,
  Wrench,
  Undo2,
} from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';

import { useFamilyGraph } from '@/contexts/FamilyGraphContext';
import { useAuth } from '@/contexts/AuthContext';
import { useCommandCenterAuthority } from '@/hooks/useCommandCenterAuthority';
import {
  isIntelligenceEnabled,
  canRunIntelligenceDiagnostics,
} from '@/lib/intelligence/featureFlag';
import {
  runTreeHealthCheck,
  type TreeHealthReport,
  type IssueSeverity,
  type TreeIssue,
} from '@/lib/intelligence/treeHealthCheck';
import {
  buildRepairProposal,
  applyRepair,
  undoLastRepair,
  hasUndoableRepair,
  type RepairProposal,
} from '@/lib/intelligence/repairActions';
import ReviewFixModal from '@/components/intelligence/ReviewFixModal';

const severityBadge = (severity: IssueSeverity) => {
  const map: Record<
    IssueSeverity,
    { cls: string; icon: React.ElementType; label: string }
  > = {
    high: {
      cls: 'bg-rose-50 text-rose-700 border-rose-200',
      icon: XCircle,
      label: 'High',
    },
    medium: {
      cls: 'bg-amber-50 text-amber-800 border-amber-200',
      icon: AlertTriangle,
      label: 'Medium',
    },
    low: {
      cls: 'bg-slate-50 text-slate-600 border-slate-200',
      icon: CheckCircle2,
      label: 'Low',
    },
  };
  const { cls, icon: Icon, label } = map[severity];
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wide ${cls}`}
    >
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

const TreeIntelligenceCard: React.FC = () => {
  // Hooks always run — early-return AFTER hooks per React rules.
  const { members, memories, photos, comments, viewerMemberId, effectiveOwnerUserId, loading, refresh } =
    useFamilyGraph();
  const auth = useAuth();
  const ownerUserId = auth?.user?.id ?? null;

  const commandCenter =
    useCommandCenterAuthority(
      !!ownerUserId,
    );

  const [report, setReport] = useState<TreeHealthReport | null>(null);
  const [running, setRunning] = useState(false);
  const [lastRunAt, setLastRunAt] = useState<number | null>(null);

  const [activeProposal, setActiveProposal] = useState<RepairProposal | null>(null);
  const [applying, setApplying] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [undoNonce, setUndoNonce] = useState(0); // re-evaluates hasUndoableRepair after undo

  const enabled = isIntelligenceEnabled();
  const canRun =
    canRunIntelligenceDiagnostics(
      commandCenter.allowed,
    );

  const runCheck = useCallback(() => {
    setRunning(true);
    try {
      const r = runTreeHealthCheck(members, viewerMemberId);
      setReport(r);
      setLastRunAt(Date.now());
    } finally {
      setRunning(false);
    }
  }, [members, viewerMemberId]);

  const headerSubtitle = useMemo(() => {
    if (!report) {
      return `Heuristic-only analysis of your loaded family graph (${members.length} relatives). Read-only — never modifies your tree.`;
    }
    return report.summary;
  }, [report, members.length]);

  const proposalForIssue = useCallback(
    (issue: TreeIssue): RepairProposal | null =>
      buildRepairProposal({
        issue,
        members,
        memories,
        photos,
        comments,
        viewerUserId: ownerUserId,
        effectiveOwnerUserId,
      }),
    [members, memories, photos, comments, ownerUserId, effectiveOwnerUserId],
  );

  const handleReviewFix = useCallback((issue: TreeIssue) => {
    const proposal = proposalForIssue(issue);
    if (!proposal) {
      toast.error('No safe repair is available for this issue. Please resolve it manually from the Family Tree.');
      return;
    }
    setActiveProposal(proposal);
  }, [proposalForIssue]);

  const handleCancelFix = useCallback(() => {
    if (applying) return;
    setActiveProposal(null);
  }, [applying]);

  const handleApplyFix = useCallback(async () => {
    if (!activeProposal || !ownerUserId) return;
    setApplying(true);
    try {
      const result = await applyRepair({
        proposal: activeProposal,
        ownerUserId,
        commandCenterOperationalAuthority:
          commandCenter.allowed,
      });
      if (result.success) {
        toast.success(result.message);
        // Refresh the loaded family graph so the visible UI reflects the
        // new state without a manual reload (STABILITY §5 contract).
        try { await refresh(); } catch { /* non-fatal */ }
        // Re-run the health check so the fixed issue disappears from the list.
        runCheck();
        setUndoNonce((n) => n + 1);
        setActiveProposal(null);
      } else {
        toast.error(result.message + (result.error ? ` (${result.error})` : ''));
      }
    } catch (err: any) {
      toast.error(err?.message ?? 'Repair failed.');
    } finally {
      setApplying(false);
    }
  }, [
    activeProposal,
    ownerUserId,
    commandCenter.allowed,
    refresh,
    runCheck,
  ]);

  const handleUndo = useCallback(async () => {
    if (!ownerUserId) return;
    setUndoing(true);
    try {
      const result =
        await undoLastRepair(
          ownerUserId,
          commandCenter.allowed,
        );
      if (result.success) {
        toast.success(result.message);
        try { await refresh(); } catch { /* non-fatal */ }
        runCheck();
        setUndoNonce((n) => n + 1);
      } else {
        toast.error(result.message + (result.error ? ` (${result.error})` : ''));
      }
    } finally {
      setUndoing(false);
    }
  }, [
    ownerUserId,
    commandCenter.allowed,
    refresh,
    runCheck,
  ]);

  const undoAvailable = useMemo(
    () => hasUndoableRepair(ownerUserId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ownerUserId, undoNonce, report, lastRunAt],
  );

  // Feature-flag gate. When disabled (default), render nothing — there
  // must be NO user-facing trace of the intelligence layer.
  if (!enabled) return null;
  if (commandCenter.loading) return null;
  // Only active Command Center principals may use governed diagnostics
  // and the existing whitelist-controlled repair workflow.
  if (!canRun) return null;

  return (
    <Card className="border-fuchsia-200 bg-gradient-to-br from-fuchsia-50/40 to-pink-50/40">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base flex-wrap">
              <Brain className="w-5 h-5 text-fuchsia-600" />
              Tree Intelligence Check
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-fuchsia-100 text-fuchsia-700 border border-fuchsia-200 text-[10px] font-semibold">
                <ShieldCheck className="w-3 h-3" />
                Command Center
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 text-[10px] font-semibold uppercase">
                V1 · Governed operational repair
              </span>
            </CardTitle>
            <CardDescription className="mt-1">{headerSubtitle}</CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {undoAvailable && (
              <Button
                variant="outline"
                onClick={handleUndo}
                disabled={undoing || running}
                className="rounded-full gap-2 border-slate-300"
              >
                {undoing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Undo2 className="w-4 h-4" />
                )}
                Undo last repair
              </Button>
            )}
            <Button
              onClick={runCheck}
              disabled={running || loading || members.length === 0}
              className="rounded-full gap-2 bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white"
            >
              {running ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <PlayCircle className="w-4 h-4" />
              )}
              Run Tree Intelligence Check
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!report && (
          <div className="text-sm text-slate-500 italic">
            No intelligence check has been run yet. Click the button above
            to scan your family graph for disconnected members, likely
            duplicates, dangling parent links, and other structural
            issues. Each detected issue with a safe, low-risk fix shows a{' '}
            <strong>Review Fix</strong> button — nothing is changed until
            you click <strong>Apply Fix</strong> inside the review modal.
          </div>
        )}
        {report && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <span>
                Last run:{' '}
                {lastRunAt
                  ? new Date(lastRunAt).toLocaleString(undefined, {
                      hour: 'numeric',
                      minute: '2-digit',
                      month: 'short',
                      day: 'numeric',
                    })
                  : '—'}
              </span>
              <span>·</span>
              <span>
                {report.counts.high} high · {report.counts.medium} medium ·{' '}
                {report.counts.low} low
              </span>
            </div>
            {report.issues.length === 0 ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  Looks great — no structural issues detected across{' '}
                  {report.memberCount} relatives.
                </div>
              </div>
            ) : (
              <ul className="space-y-2.5">
                {report.issues.map((issue) => {
                  const proposal = proposalForIssue(issue);
                  const eligible = !!proposal;
                  return (
                    <li
                      key={issue.id}
                      className="rounded-lg border border-slate-200 bg-white p-3"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-slate-900 text-sm">
                              {issue.title}
                            </span>
                            {severityBadge(issue.severity)}
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-50 text-slate-600 border border-slate-200 text-[10px] font-medium">
                              {issue.category}
                            </span>
                            {eligible && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-semibold">
                                Safe to repair
                              </span>
                            )}
                          </div>
                          {issue.affectedNames.length > 0 && (
                            <div className="mt-1 text-xs text-slate-500">
                              Affected:{' '}
                              <span className="text-slate-700">
                                {issue.affectedNames.slice(0, 6).join(', ')}
                                {issue.affectedNames.length > 6
                                  ? ` and ${issue.affectedNames.length - 6} more`
                                  : ''}
                              </span>
                            </div>
                          )}
                          <div className="mt-1.5 text-xs text-slate-600">
                            <span className="font-semibold text-slate-700">
                              Likely cause:
                            </span>{' '}
                            {issue.likelyCause}
                          </div>
                          <div className="mt-1 text-xs text-slate-600">
                            <span className="font-semibold text-slate-700">
                              Recommended next step:
                            </span>{' '}
                            {issue.recommendedNextStep}
                          </div>
                        </div>
                        {eligible && (
                          <div className="shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleReviewFix(issue)}
                              className="rounded-full border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50 gap-1.5"
                            >
                              <Wrench className="w-3.5 h-3.5" />
                              Review Fix
                            </Button>
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-600">
              V1 is report-first. Only low-risk repairs (approving a pending
              member with a known anchor, removing an empty placeholder
              duplicate with no memories / photos / comments / claim,
              syncing an accepted invite's status, binding your account to
              an unclaimed self-anchor) expose a Review Fix button. Every
              other recommendation must be applied manually from the
              existing family-tree UI so the locked relationship engine,
              tree layout, and labels remain the source of truth.
            </div>
          </div>
        )}
      </CardContent>

      <ReviewFixModal
        open={!!activeProposal}
        proposal={activeProposal}
        applying={applying}
        onCancel={handleCancelFix}
        onApply={handleApplyFix}
      />
    </Card>
  );
};

export default TreeIntelligenceCard;
